import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import ArcadeGameShell from '../ArcadeGameShell';
import { useArcadeAgentSession } from '../useArcadeAgentSession';
import { ManualPlayChrome, useManualPlay, usePlayKeyboard } from '../manualPlay';
import { useGmSession } from '../useGmSession';
import { GAME_BOILERPLATE } from './AGENT_BOILERPLATE';
import { ARCADE } from '../../../utils/config';
import { useManualRunRecorder } from '../useManualRunRecorder';
import {
    createPongSession,
    deletePongSession,
    movePongSession,
    HIT_POINTS,
    GOAL_POINTS,
    MAX_GOALS,
    MAX_SCORE,
    PADDLE_SPEED,
    BALL_SPEED,
    PADDLE_HEIGHT,
} from './pongGmClient';
import { gmStartErrorMessage } from '../gmLease';
import { COLOR, GAME_ACCENT } from '../ui/tokens';

const GAME_ID = 'pong';
const { UPLOAD_URL } = ARCADE.pong;
const ACCENT = GAME_ACCENT.pong;
const HINT = '↑↓ / W S move paddle · Esc pause';
const TICK_MS = 50;

export { HIT_POINTS, GOAL_POINTS, MAX_GOALS, MAX_SCORE };

const clamp = (val, min, max) => Math.max(min, Math.min(max, val));

/** Kept for unit tests — runtime play uses the GM. */
export function applyPongHit(score, who = 'player') {
    return { ...score, [who]: (score[who] || 0) + HIT_POINTS };
}

export function applyPongGoal(score, goals, scorer = 'player') {
    return {
        score: { ...score, [scorer]: (score[scorer] || 0) + GOAL_POINTS },
        goals: { ...goals, [scorer]: (goals[scorer] || 0) + 1 },
    };
}

export function isPongMatchOver(goals, maxGoals = MAX_GOALS) {
    return (goals.player || 0) >= maxGoals || (goals.opponent || 0) >= maxGoals;
}

export function matchOverMessage(goals) {
    if (goals.player > goals.opponent) return 'Match won';
    if (goals.player < goals.opponent) return 'Match lost';
    return 'Draw';
}

export const freshMatch = () => ({
    paddles: { player: 0.5, opponent: 0.5 },
    ball: { x: 0.5, y: 0.5, vx: -BALL_SPEED, vy: BALL_SPEED },
    score: { player: 0, opponent: 0 },
    goals: { player: 0, opponent: 0 },
    stepCount: 0,
    isGameOver: false,
    message: 'New Pong match started',
});

/** Local physics mirror for unit tests only (matches pong_gm.py). */
export function stepPongPhysics(state, action) {
    const paddles = { ...state.paddles };
    const ball = { ...state.ball };
    let score = { ...state.score };
    let goals = { ...state.goals };

    if (action === 'up') paddles.player -= PADDLE_SPEED;
    else if (action === 'down') paddles.player += PADDLE_SPEED;
    paddles.player = clamp(paddles.player, PADDLE_HEIGHT / 2, 1 - PADDLE_HEIGHT / 2);

    const diff = ball.y - paddles.opponent;
    paddles.opponent = clamp(
        paddles.opponent + clamp(diff, -PADDLE_SPEED, PADDLE_SPEED),
        PADDLE_HEIGHT / 2,
        1 - PADDLE_HEIGHT / 2,
    );

    ball.x += ball.vx;
    ball.y += ball.vy;
    if (ball.y <= 0) { ball.y = -ball.y; ball.vy = Math.abs(ball.vy); }
    else if (ball.y >= 1) { ball.y = 2 - ball.y; ball.vy = -Math.abs(ball.vy); }

    const half = PADDLE_HEIGHT / 2;
    const hit = (center, atLeft) => {
        if (Math.abs(ball.y - center) <= half) {
            ball.vx = atLeft ? Math.abs(ball.vx) : -Math.abs(ball.vx);
            const offset = (ball.y - center) / half;
            ball.vy = clamp(ball.vy + offset * 0.01, -0.04, 0.04);
            return true;
        }
        return false;
    };

    let message = state.message;
    if (ball.x <= 0.03 && ball.vx < 0) {
        if (hit(paddles.player, true)) {
            score = applyPongHit(score, 'player');
            message = 'Save';
        } else {
            ({ score, goals } = applyPongGoal(score, goals, 'opponent'));
            message = 'Opponent scored';
            ball.x = 0.5; ball.y = 0.5; ball.vx = Math.abs(ball.vx); ball.vy = BALL_SPEED;
        }
    } else if (ball.x >= 0.97 && ball.vx > 0) {
        if (hit(paddles.opponent, false)) {
            score = applyPongHit(score, 'opponent');
            message = 'Opponent save';
        } else {
            ({ score, goals } = applyPongGoal(score, goals, 'player'));
            message = 'Player scored';
            ball.x = 0.5; ball.y = 0.5; ball.vx = -Math.abs(ball.vx); ball.vy = -BALL_SPEED;
        }
    }

    let isGameOver = false;
    if (isPongMatchOver(goals)) {
        isGameOver = true;
        message = matchOverMessage(goals);
    }

    return {
        paddles,
        ball,
        score,
        goals,
        stepCount: (state.stepCount || 0) + 1,
        isGameOver,
        message,
    };
}

const Paddle = ({ position, label, color }) => (
    <Box sx={{
        position: 'absolute',
        left: label === 'Player' ? 8 : 'calc(100% - 18px)',
        top: `calc(${clamp(position, 0, 1) * 100}% - 45px)`,
        width: 10,
        height: 90,
        borderRadius: 2,
        background: color,
    }}
    />
);

const Ball = ({ x, y }) => (
    <Box sx={{
        position: 'absolute',
        left: `calc(${clamp(x, 0, 1) * 100}% - 6px)`,
        top: `calc(${clamp(y, 0, 1) * 100}% - 6px)`,
        width: 12,
        height: 12,
        borderRadius: '50%',
        background: '#FFD700',
        boxShadow: '0 0 12px rgba(255,215,0,0.6)',
    }}
    />
);

const formatPongScore = (e) => {
    const pts = e.score ?? e.player_score ?? 0;
    const gp = e.player_goals;
    const go = e.opponent_goals;
    if (gp != null && go != null) return `${pts} (${gp}–${go})`;
    return String(pts);
};

const emptyView = () => ({
    paddles: { player: 0.5, opponent: 0.5 },
    ball: { x: 0.5, y: 0.5 },
    score: { player: 0, opponent: 0 },
    goals: { player: 0, opponent: 0 },
    stepCount: 0,
    isGameOver: false,
    message: '',
});

const Pong = () => {
    const play = useManualPlay();
    const [view, setView] = useState(emptyView);
    const viewRef = useRef(view);
    const actionRef = useRef('stay');
    const phaseRef = useRef(play.phase);
    const inFlightRef = useRef(false);

    const session = useArcadeAgentSession({ gameId: GAME_ID, uploadUrl: UPLOAD_URL });
    const recorder = useManualRunRecorder({ gameId: GAME_ID, session });
    const gm = useGmSession({
        createSession: createPongSession,
        deleteSession: deletePongSession,
    });

    useEffect(() => { viewRef.current = view; }, [view]);
    useEffect(() => { phaseRef.current = play.phase; }, [play.phase]);

    const applyGm = useCallback((data) => {
        const next = {
            paddles: data.paddles || { player: 0.5, opponent: 0.5 },
            ball: data.ball || { x: 0.5, y: 0.5 },
            score: data.score || { player: 0, opponent: 0 },
            goals: data.goals || { player: 0, opponent: 0 },
            stepCount: typeof data.step_count === 'number' ? data.step_count : 0,
            isGameOver: Boolean(data.is_game_over),
            message: data.message || '',
        };
        viewRef.current = next;
        setView(next);
        return next;
    }, []);

    const startGame = useCallback(async () => {
        try {
            gm.setBusy(true);
            const data = await gm.createFreshSession();
            const next = applyGm(data);
            recorder.reset({
                init_state: {
                    paddles: { ...next.paddles },
                    ball: { ...next.ball },
                    score: { ...next.score },
                    goals: { ...next.goals },
                },
            });
            actionRef.current = 'stay';
            play.startGame();
        } catch (err) {
            console.error(err);
            setView((v) => ({ ...v, message: gmStartErrorMessage(err) }));
        } finally {
            gm.setBusy(false);
        }
    }, [applyGm, gm, play, recorder]);

    const endGame = useCallback(async () => {
        const last = viewRef.current;
        // Flip phase before clearSession so the tick interval cannot send
        // another /move after End (that late response used to re-track the lease).
        phaseRef.current = 'ended';
        actionRef.current = 'stay';
        play.endGame({
            score: `${last.score.player} – ${last.score.opponent}`,
            scoreLabel: 'You – Opponent',
        });
        await gm.clearSession();
        const next = emptyView();
        viewRef.current = next;
        setView(next);
    }, [gm, play]);

    const tick = useCallback(async () => {
        if (phaseRef.current !== 'playing') return;
        if (viewRef.current.isGameOver) return;
        if (inFlightRef.current) return;
        const sid = gm.sessionIdRef.current;
        if (!sid) return;
        inFlightRef.current = true;
        try {
            const data = await movePongSession(sid, actionRef.current);
            const next = applyGm(data);
            recorder.recordTick({
                action: actionRef.current,
                ball: { x: next.ball.x, y: next.ball.y },
                paddles: { player: next.paddles.player, opponent: next.paddles.opponent },
                score: { player: next.score.player, opponent: next.score.opponent },
                goals: { player: next.goals.player, opponent: next.goals.opponent },
            });
            if (next.isGameOver) {
                play.markOver();
                recorder.finalizeAndUpload({
                    score: next.score.player,
                    extra: {
                        player_score: next.score.player,
                        opponent_score: next.score.opponent,
                        player_goals: next.goals.player,
                        opponent_goals: next.goals.opponent,
                    },
                });
                await gm.clearSession();
            }
        } catch (err) {
            console.warn('Pong GM tick failed', err);
        } finally {
            inFlightRef.current = false;
        }
    }, [applyGm, gm, play, recorder]);

    useEffect(() => {
        if (play.phase !== 'playing') return undefined;
        const id = setInterval(() => { tick(); }, TICK_MS);
        return () => clearInterval(id);
    }, [play.phase, tick]);

    const onKey = useCallback((e) => {
        if (['ArrowUp', 'w', 'W'].includes(e.key)) {
            actionRef.current = 'up';
            e.preventDefault();
        } else if (['ArrowDown', 's', 'S'].includes(e.key)) {
            actionRef.current = 'down';
            e.preventDefault();
        }
    }, []);

    useEffect(() => {
        const up = (e) => {
            if (['ArrowUp', 'w', 'W', 'ArrowDown', 's', 'S'].includes(e.key)) {
                actionRef.current = 'stay';
            }
        };
        window.addEventListener('keyup', up);
        return () => window.removeEventListener('keyup', up);
    }, []);

    usePlayKeyboard({
        phase: play.phase,
        togglePause: play.togglePause,
        onKey,
        deps: [],
    });

    return (
        <ArcadeGameShell
            title="Pong"
            titleAccent="Rally"
            subtitle="Hits score +25, goals +200 — first to 5 goals ends the match"
            accentColor={ACCENT}
            boilerplate={GAME_BOILERPLATE.pong}
            session={session}
            formatScore={formatPongScore}
        >
            <ManualPlayChrome
                phase={play.phase}
                result={play.result}
                onStart={startGame}
                onPause={play.togglePause}
                onEnd={endGame}
                accentColor={ACCENT}
                controlsHint={HINT}
            >
                <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                        <Chip label={`Score: ${view.score.player}`} size="small" sx={{ color: ACCENT, borderColor: ACCENT }} variant="outlined" />
                        <Chip label={`Goals: ${view.goals.player}–${view.goals.opponent}`} size="small" variant="outlined" sx={{ color: COLOR.textMuted, borderColor: COLOR.borderStrong }} />
                        <Chip label={`Opp: ${view.score.opponent}`} size="small" variant="outlined" sx={{ color: COLOR.textMuted, borderColor: COLOR.borderStrong }} />
                        <Chip label={`Steps: ${view.stepCount}`} size="small" variant="outlined" sx={{ color: COLOR.textMuted, borderColor: COLOR.borderStrong }} />
                        <Chip label={view.isGameOver ? 'Game Over' : 'In Progress'} size="small" color={view.isGameOver ? 'warning' : 'success'} />
                    </Stack>
                    {view.message && (
                        <Typography sx={{ color: COLOR.textMuted, mb: 1, fontSize: '0.85rem' }}>{view.message}</Typography>
                    )}
                    <Box sx={{
                        position: 'relative',
                        width: '100%',
                        paddingTop: '56%',
                        background: 'linear-gradient(135deg, rgba(0,0,0,0.9), rgba(0,0,0,0.7))',
                        borderRadius: 3,
                        border: '2px solid rgba(255,255,255,0.1)',
                        overflow: 'hidden',
                    }}
                    >
                        <Paddle position={view.paddles.player} label="Player" color="#4ECDC4" />
                        <Paddle position={view.paddles.opponent} label="Opponent" color="#FF6B6B" />
                        <Ball x={view.ball.x} y={view.ball.y} />
                    </Box>
                </Box>
            </ManualPlayChrome>
        </ArcadeGameShell>
    );
};

export default Pong;
